import { useForm, FormProvider, type Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import type { SourceFolder } from "../sources-types";
import { FTPInput } from "../../../../../components-new";
import { formTitleClass } from "./addSourceStyles";
import { useSourceForm } from "./useSourceForm";
import { SourceFormFooter } from "./SourceFormFooter";

type CreateFolderFormValues = { name: string };

const schema = yup.object({ name: yup.string().required("Folder name is required").trim() });

interface CreateFolderFormProps {
  parentId: string | null;
  editNode?: SourceFolder | null;
  onSuccess: () => void;
  onCancel: () => void;
}

export default function CreateFolderForm({ parentId, editNode, onSuccess, onCancel }: CreateFolderFormProps) {
  const { isEdit, isUploading, submitSource, renameNode } = useSourceForm(parentId, editNode ?? null, onSuccess);

  const methods = useForm<CreateFolderFormValues>({
    resolver: yupResolver(schema) as Resolver<CreateFolderFormValues>,
    defaultValues: { name: editNode?.name ?? "" },
  });

  const onSubmit = async (data: CreateFolderFormValues) => {
    const name = data.name?.trim();
    if (!name) return;
    if (isEdit) {
      await renameNode(name);
      return;
    }
    await submitSource({ type: "folder", name });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        <h3 className={formTitleClass}>{isEdit ? "Edit Folder" : "Create Folder"}</h3>
        <FTPInput name="name" label="Folder name" className="mb-3" size="small" />
        <SourceFormFooter
          onCancel={onCancel}
          submitLabel={isEdit ? "Save" : isUploading ? "Creating…" : "Create"}
          disabled={!methods.watch("name")?.trim() || (isUploading && !isEdit)}
        />
      </form>
    </FormProvider>
  );
}
